import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CenterMessage from '../components/ui/CenterMessage';
import EPRMagicForm from './EPRMagicForm';
import Review360MagicForm from './Review360MagicForm';
import AppraisalSignMagicForm from './AppraisalSignMagicForm';
import AppraisalViewMagicForm from './AppraisalViewMagicForm';
import { verifyToken, markTokenOpened, MagicLinkError } from '../services/magicLinkService';
import type { MagicLinkToken } from '../types/magicLinkToken';

/**
 * /m/:token — single dispatch route for HR-framework magic links.
 *
 * Verifies the token, then renders the right page for token.action. Per
 * CLAUDE.md §H, this is the only public entry point for hr_v1 reviewer
 * surfaces. Existing OPTICS routes (/r/:token, /360/:token) remain separate.
 *
 * For C3b, only EPR self/manager actions render fully; the three remaining
 * actions render a placeholder until later chunks flesh them out.
 */

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; token: MagicLinkToken }
  | { kind: 'error'; code: string; message: string };

const MagicLinkRoute: React.FC = () => {
  const { token: rawToken } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    if (!rawToken) {
      setState({ kind: 'error', code: 'not_found', message: 'Token tidak ada dalam URL.' });
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const tok = await verifyToken(rawToken);
        if (!alive) return;
        setState({ kind: 'ready', token: tok });
        // Fire-and-forget — open marker is informational only
        void markTokenOpened(rawToken);
      } catch (e) {
        if (!alive) return;
        if (e instanceof MagicLinkError) {
          setState({ kind: 'error', code: e.code, message: e.message });
        } else {
          setState({
            kind: 'error',
            code: 'unknown',
            message: (e as Error).message ?? 'Terjadi kesalahan tak terduga.',
          });
        }
      }
    })();
    return () => { alive = false; };
  }, [rawToken]);

  if (state.kind === 'loading') {
    return <CenterMessage tone="info" title="Memverifikasi link..." />;
  }

  if (state.kind === 'error') {
    return renderErrorPage(state.code, state.message);
  }

  const { token } = state;
  switch (token.action) {
    case 'epr_self_submit':
      return <EPRMagicForm token={token} mode="employee" />;
    case 'epr_manager_submit':
      return <EPRMagicForm token={token} mode="manager" />;
    case 'review360_submit':
      return <Review360MagicForm token={token} />;
    case 'appraisal_sign':
      return <AppraisalSignMagicForm token={token} />;
    case 'result_view':
      return <AppraisalViewMagicForm token={token} />;
    default:
      return (
        <CenterMessage
          tone="error"
          title="Unknown action"
          body={`This token has action '${(token as MagicLinkToken).action}' which is not handled yet.`}
        />
      );
  }
};

// ─── Helpers ────────────────────────────────────────────────────────

function renderErrorPage(code: string, message: string): React.ReactElement {
  switch (code) {
    case 'not_found':
      return (
        <CenterMessage
          tone="error"
          title="Invalid link"
          body="This link is not in the system. Contact HR to re-issue the link."
        />
      );
    case 'expired':
      return (
        <CenterMessage
          tone="warning"
          title="Link expired"
          body={message}
        />
      );
    case 'used':
      return (
        <CenterMessage
          tone="info"
          title="Link already used"
          body="This link can only be used once. Contact HR if you need to change your submission."
        />
      );
    case 'revoked':
      return (
        <CenterMessage
          tone="error"
          title="Link revoked"
          body={message}
        />
      );
    default:
      return (
        <CenterMessage
          tone="error"
          title="Cannot open link"
          body={message}
        />
      );
  }
}

export default MagicLinkRoute;
